// RuleFilters.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
    Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControl, InputLabel, MenuItem, Select, Snackbar, Alert, Stack,
    Switch, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, IconButton, Tooltip, FormControlLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";

const RULE_OPTIONS = [
    { ruleName: "AllPhrasesMustExist", text: "(A) All Phrases Must Exist", description: "All of the phrases must exist for the rule to validate." },
    { ruleName: "AnyPhraseMustExist", text: "(B) Any Phrase Must Exist", description: "Any of the phrases must exist for the rule to validate." },
    { ruleName: "AllPhrasesMustExistWithoutAllOther", text: "(C) All Phrases Must Exist, Only If No Secondary", description: "All phrases must exist and no secondary phrase can exist", is_secondary: true },
    { ruleName: "AnyPhraseMustExistButOnlyNoneOfOther", text: "(D) Any Phrase Must Exist, Only If No Secondary", description: "Any phrase must exist, if no other phrase exists " },
    { ruleName: "AllMustExistWithAnyOfOther", text: "(E) All phrases Must Exist, With Any Of Secondary", description: "All phrase must exist with any other phrase " },
    { ruleName: "AllPhraseMustExistButWithoutAllOfOther", text: "(F) All Phrases Must Exist, Without All Secondary", description: "All phrases must exist with no other phrase existing" },
    { ruleName: "RequirePercentageOfPhrasesToExist", text: "(G) A Percentage Of Phrases Must Exist", description: "A percentage of phrases must exist", require_pct: true },
    { ruleName: "NoPhraseCanExist", text: "(I) Exclude Any Phrase ", description: "Will not validate if any phrase is found", require_pct: false },
];

const NEW_TEMPLATE = {
    type: "AllPhrasesMustExist",
    phrases: [],
    ignore_case: true,
    word_boundary: true,
    // filter_type removed entirely
};

function linesToArray(s) {
    return s
        .split("\n")
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
}
function arrayToLines(a) {
    return (a ?? []).join("\n");
}
function getRuleOption(ruleName) {
    if (!ruleName) return undefined;
    return RULE_OPTIONS.find((r) => r.ruleName === ruleName);
}
const rawClone = (list) => JSON.parse(JSON.stringify(list));

// add this helper
function migrateKeywords(rules, keywordsObj) {
    const updatedRules = [...rules];

    if (keywordsObj.version === "1") {
        const phrases = (keywordsObj.keywords || []).map(k => k.keyword);

        const newRule = {
            type: "AnyPhraseMustExist",
            phrases,
            ignore_case: true,
            version: "2",
            word_boundary: true,
        };

        // remove any leftover v1 keyword rule (defensive)
        const filtered = updatedRules.filter(r => r.version !== "1");

        filtered.push(newRule);
        return filtered;
    }

    return updatedRules;
}

function v1ToV2Rule(v1) {
    const list = Array.isArray(v1?.keywords) ? v1.keywords : [];
    const phrases = list.map(k =>
        typeof k === "string" ? k : (k && typeof k.keyword === "string" ? k.keyword : "")
    ).filter(Boolean);

    return {
        type: "AnyPhraseMustExist",
        phrases,
        ignore_case: true,
        version: "2",
    };
}

// Normalize "version" to a string ("1"/"2") if present
function getVersion(x) {
    const v = x && x.version;
    if (v == null) return null;
    return String(v).trim();
}

/**
 * Migrates any input shape to a v2 rules array:
 * - Root array: mixed entries (v1/v2/legacy arrays)
 * - Root object with { keywords: [...] } (your new example)
 * - Single v1 object { version:"1", keywords:[...] }
 * - Legacy bare arrays of strings or {keyword:""} objects
 *
 * @param {any} input
 * @returns {Array} v2Rules
 */
export function migrateKeywordsToV2Rules(input) {
    const out = [];

    // Helper to push a migrated item
    const handleOne = (item) => {
        if (Array.isArray(item)) {
            // Legacy bare array -> treat as v1 wrapper
            out.push(v1ToV2Rule({ version: "1", keywords: item }));
            return;
        }
        if (!item || typeof item !== "object") return;

        const v = getVersion(item);
        if (v === "1") {
            out.push(v1ToV2Rule(item));
            return;
        }
        if (v === "2") {
            // Already a v2 rule; keep as-is
            out.push(item);
            return;
        }

        // No explicit version:
        // Try to detect legacy v1 shape { keywords: [...] }
        if (Array.isArray(item.keywords)) {
            out.push(v1ToV2Rule({ version: "1", keywords: item.keywords }));
            return;
        }

        // Already looks like a v2 rule but no version? Keep and stamp v2.
        if (item.type && Array.isArray(item.phrases)) {
            out.push({ ...item, version: "2" });
            return;
        }
    };

    // Case A: root is an array (e.g., keywords: [ {...}, {...} ])
    if (Array.isArray(input)) {
        input.forEach(handleOne);
        return out;
    }

    // Case B: root is an object with .keywords (array or object)
    if (input && typeof input === "object" && input.keywords != null) {
        const k = input.keywords;
        if (Array.isArray(k)) {
            k.forEach(handleOne);
        } else {
            handleOne(k);
        }
        return out;
    }

    // Case C: root is a single v1/v2 object or legacy array
    handleOne(input);
    return out;
}

export default function RuleFilters({ initialFilterRulesJson, onSaved }) {
    // Parse initial rules
    const initialRules = useMemo(() => {
        if (!initialFilterRulesJson) return [];
        try {
            const parsed = initialFilterRulesJson;   // <-- parse!
            // If it's already v2 array, keep as-is
            if (Array.isArray(parsed) && parsed.every(r => r && r.version === "2")) {
                return parsed;
            }
            // Otherwise migrate to clean v2 rules array
            const migrated = migrateKeywordsToV2Rules(parsed);
            console.log("migrated rules", migrated);
            return migrated;
        } catch (e) {
            console.error("Failed to parse initialFilterRulesJson", e);
            return [];
        }
    }, [initialFilterRulesJson]);

// 2) lazy-init state with initialRules
    const [rules, setRules] = useState(() => initialRules);

// 3) keep local state in sync if the prop changes later
    useEffect(() => {
        setRules(initialRules);
        onSaved(initialRules);
    }, [initialRules]);
    

    // Edit dialog state
    const [open, setOpen] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    // Form fields (no filter_type anywhere)
    const [fType, setFType] = useState(NEW_TEMPLATE.type);
    const [fPhrases, setFPhrases] = useState("");
    const [fSecondary, setFSecondary] = useState("");
    const [fMustNotExist, setFMustNotExist] = useState("");
    const [fIgnoreCase, setFIgnoreCase] = useState(true);
    const [fRequiredPct, setFRequiredPct] = useState("");
    const [fWordBoundary, setFWordBoundary] = useState(true);
    // Validation
    const [errors, setErrors] = useState({});
    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

    const selectedRuleOption = getRuleOption(fType);

    const resetForm = useCallback((seed, newMode = false) => {
        const r = seed ?? NEW_TEMPLATE;
        setFType(r.type);
        setFPhrases(arrayToLines(r.phrases));
        setFSecondary(arrayToLines(r.secondary_phrases));
        setFMustNotExist(arrayToLines(r.phrases_must_not_exist));
        setFIgnoreCase(r.ignore_case ?? true);
        setFWordBoundary(r.word_boundary ?? true);
        setFRequiredPct(
            r.required_pct !== undefined && r.required_pct !== null ? String(r.required_pct) : ""
        );
        setErrors({});
        setIsNew(newMode);
    }, []);

    function handleAddNew() {
        setEditingIndex(null);
        resetForm(NEW_TEMPLATE, true);
        setOpen(true);
    }

    function handleEdit(idx) {
        const r = rules[idx];
        setEditingIndex(idx);
        resetForm(r, false);
        setOpen(true);
    }

    function validate() {
        const e = {};
        if (linesToArray(fPhrases).length === 0) e.phrases = "Please enter at least one required phrase.";
        if (selectedRuleOption?.require_pct) {
            const val = parseFloat(fRequiredPct);
            if (Number.isNaN(val)) e.required_pct = "Required percentage is needed for this rule.";
            else if (val < 0 || val > 100) e.required_pct = "Percentage must be between 0 and 100.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    function buildRuleFromForm() {
        const out = {
            type: fType,
            phrases: linesToArray(fPhrases),
            ignore_case: !!fIgnoreCase,
            word_boundary: !!fWordBoundary,
        };
        if (selectedRuleOption?.is_secondary) {
            const sec = linesToArray(fSecondary);
            if (sec.length > 0) out.secondary_phrases = sec;
            else delete out.secondary_phrases;
        } else {
            delete out.secondary_phrases;
        }
        if (selectedRuleOption?.require_pct) {
            const pct = parseFloat(fRequiredPct);
            if (!Number.isNaN(pct)) out.required_pct = pct;
        } else {
            delete out.required_pct;
        }
        const mustNot = linesToArray(fMustNotExist);
        if (mustNot.length > 0) out.phrases_must_not_exist = mustNot;
        else delete out.phrases_must_not_exist;

        return out;
    }

    function handleDialogClose() {
        // No state change → no onSaved here (useEffect handles broadcasting on actual changes)
        setOpen(false);
    }

    function handleSaveRow() {
        if (!validate()) return;
        const nextRule = buildRuleFromForm();

        const nextList = (() => {
            const clone = [...rules];
            if (isNew || editingIndex === null || editingIndex < 0) clone.push(nextRule);
            else clone[editingIndex] = nextRule;
            return clone;
        })();

        setRules(nextList);

        setOpen(false);
        //setSnack({ open: true, msg: "Rule saved.", severity: "success" });
        onSaved(nextList)
    }

    function handleDelete(idx) {
        let rulesRm = rules.filter((_, i) => i !== idx);
        setRules(rulesRm);
        setSnack({ open: true, msg: "Rule deleted.", severity: "success" });
        console.log(rulesRm);
        onSaved(rulesRm);
    }

    return (
        <Box component="section">
            <Card variant="outlined" sx={{ mb: 3, p:0  }}>
                <CardHeader
                    title="Rule filters"
                    action={
                        <Button startIcon={<AddIcon />} variant="contained" color="success" onClick={handleAddNew}>
                            Add New
                        </Button>
                    }
                />
                <CardContent>
                    <TableContainer>
                        <Table size="small" aria-label="rules table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Rule Type {rules.length}</TableCell>
                                    <TableCell>Phrases</TableCell>
                                    <TableCell>Phrases Must Not Exist</TableCell>
                                    <TableCell>Ignore case</TableCell>
                                    <TableCell>Word boundary</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                
                                {rules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography variant="body2" color="text.secondary">
                                                No rules yet — click “Add New” to create one.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rules.map((r, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => handleEdit(idx)} size="small">
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Box>
                                                        <Typography variant="subtitle2">
                                                            {getRuleOption(r.type)?.text ?? r.type}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{arrayToLines(r.phrases)}</pre>
                                                {r.secondary_phrases && r.secondary_phrases.length > 0 && (
                                                    <>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Typography variant="caption">Secondary:</Typography>
                                                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                              {arrayToLines(r.secondary_phrases)}
                            </pre>
                                                    </>
                                                )}
                                                {typeof r.required_pct === "number" && (
                                                    <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                                                        Required %: {r.required_pct}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                          {arrayToLines(r.phrases_must_not_exist)}
                        </pre>
                                            </TableCell>
                                            <TableCell>
                                                <Typography color={r.ignore_case ? "success.main" : "error.main"}>
                                                    {r.ignore_case ? "✓" : "✗"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography color={r.word_boundary ? "success.main" : "warning.main"}>
                                                    {r.word_boundary ? "✓" : "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Delete">
                                                    <IconButton color="error" onClick={() => handleDelete(idx)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>{isNew ? "Add rule" : "Edit rule"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="rule-type-label">Rule Type</InputLabel>
                            <Select
                                labelId="rule-type-label"
                                label="Rule Type"
                                value={fType}
                                onChange={(e) => setFType(e.target.value)}
                            >
                                {RULE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.ruleName} value={opt.ruleName}>
                                        {opt.text}
                                    </MenuItem>
                                ))}
                            </Select>
                            {selectedRuleOption?.description && (
                                <Alert sx={{ mt: 1 }} severity="info">
                                    {selectedRuleOption.description}
                                </Alert>
                            )}
                        </FormControl>

                        <FormControl fullWidth error={!!errors.phrases}>
                            <TextField
                                label="Required Phrases"
                                helperText={errors.phrases ? errors.phrases : "One phrase per line"}
                                value={fPhrases}
                                onChange={(e) => setFPhrases(e.target.value)}
                                multiline
                                minRows={5}
                            />
                        </FormControl>

                        {selectedRuleOption?.is_secondary && (
                            <TextField
                                label="Secondary Phrases"
                                helperText="One phrase per line"
                                value={fSecondary}
                                onChange={(e) => setFSecondary(e.target.value)}
                                multiline
                                minRows={5}
                            />
                        )}

                        {selectedRuleOption?.require_pct && (
                            <TextField
                                label="Required Percentage"
                                type="number"
                                inputProps={{ min: 0, max: 100, step: "any" }}
                                helperText={
                                    errors.required_pct
                                        ? errors.required_pct
                                        : "The percentage of required words (0–100)."
                                }
                                value={fRequiredPct}
                                onChange={(e) => setFRequiredPct(e.target.value)}
                                error={!!errors.required_pct}
                            />
                        )}

                        <FormControlLabel
                            control={<Switch checked={fIgnoreCase} onChange={(e) => setFIgnoreCase(e.target.checked)} />}
                            label="Ignore case"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={fWordBoundary}
                                    onChange={(e) => setFWordBoundary(e.target.checked)}
                                />
                            }
                            label="Match whole words (word boundary)"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                            When on, “db” won’t match inside “goodbyers”.
                        </Typography>

                        <TextField
                            label="Phrases Must Not Exist (optional)"
                            helperText="One phrase per line (rule fails if any exist)"
                            value={fMustNotExist}
                            onChange={(e) => setFMustNotExist(e.target.value)}
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={handleDialogClose}>Close</Button>
                    <Button variant="contained" onClick={handleSaveRow}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={2500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
                <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}

RuleFilters.propTypes = {
    initialFilterRulesJson: PropTypes.array,
    onSaved: PropTypes.func, // (rulesArray) => void
};
